<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { BatchNotice } from '@/composables/useGalleryUploadManager'
import { formatBytes } from '@/utils/formatBytes'

const props = defineProps<{
  notice: BatchNotice
}>()

const emit = defineEmits<{
  'upload-fitting': []
  cancel: []
  dismiss: []
}>()

const { t } = useI18n()
const expanded = ref(false)

const total = computed(() => props.notice.fitCount + props.notice.doesNotFit.length)
</script>

<template>
  <div
    class="rounded-xl border px-5 py-4 text-sm"
    :class="notice.needsDecision
      ? 'bg-amber-500/10 border-amber-500/30 text-amber-200'
      : 'bg-slate-800 border-slate-700 text-slate-300'"
    role="status"
    data-testid="gallery-preflight-banner"
  >
    <p v-if="notice.needsDecision" class="font-medium">
      {{ t('galleries.preflight.summary', { fits: notice.fitCount, total, remaining: formatBytes(notice.remainingBytes) }) }}
    </p>

    <div v-if="notice.doesNotFit.length > 0" class="mt-2">
      <button
        type="button"
        class="text-xs font-medium underline underline-offset-2"
        :aria-expanded="expanded"
        @click="expanded = !expanded"
      >
        {{ expanded ? t('galleries.preflight.hide_files') : t('galleries.preflight.show_files', { count: notice.doesNotFit.length }) }}
      </button>
      <ul v-if="expanded" class="mt-2 space-y-1 text-xs">
        <li v-for="file in notice.doesNotFit" :key="file.id" class="flex justify-between gap-3">
          <span class="truncate">{{ file.name }}</span>
          <span class="shrink-0">{{ formatBytes(file.bytes) }}</span>
        </li>
      </ul>
    </div>

    <div v-if="notice.rejected.length > 0" class="mt-3">
      <p class="font-medium">{{ t('galleries.preflight.rejected_title', { count: notice.rejected.length }) }}</p>
      <ul class="mt-1 space-y-1 text-xs">
        <li v-for="(file, index) in notice.rejected" :key="`${file.name}-${index}`" class="flex justify-between gap-3">
          <span class="truncate">{{ file.name }}</span>
          <span class="shrink-0">{{ t(`galleries.reasons.${file.reason}`, { max: 50 }) }}</span>
        </li>
      </ul>
    </div>

    <div class="mt-3 flex flex-wrap items-center gap-3">
      <template v-if="notice.needsDecision">
        <button
          v-if="notice.fitCount > 0"
          type="button"
          class="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
          @click="emit('upload-fitting')"
        >
          {{ t('galleries.preflight.upload_fitting', notice.fitCount) }}
        </button>
        <button
          type="button"
          class="border border-slate-600 hover:border-slate-500 text-slate-200 font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
          @click="emit('cancel')"
        >
          {{ t('galleries.preflight.cancel') }}
        </button>
      </template>
      <button
        v-else
        type="button"
        class="border border-slate-600 hover:border-slate-500 text-slate-200 font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
        @click="emit('dismiss')"
      >
        {{ t('galleries.preflight.dismiss') }}
      </button>
    </div>
  </div>
</template>
